'use client';

import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { hasPermission } from '@ravago/shared/next-data-access';
import { ShoppingCartUtils } from '@ravago/shared/next-data-access/client';
import { type FeatureToggle } from '@ravago/shared/page-data/models/config';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { PostShoppingCartCheckout, ShoppingCartProblem } from '@ravago/shared/page-data/models/features';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Calendar } from '@ravago/shared/radiance/components/calendar/components/Calendar/Calendar';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { MultiLineText } from '@ravago/shared/radiance/components/typography/components/MultiLineText/MultiLineText';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { HttpErrorResponse } from '@angular/common/http';
import { Instant, LocalDate, LocalDateTime } from '@js-joda/core';

import { TrackingUtils } from '../../../../../utils/tracking/tracking.utils';

interface Props {
   features: FeatureToggle[];
   legalAddress: Address;
   deliveryAddress: Address;
   locale: string;
   hasProblems: boolean;
   changeCheckoutStatus: (status: 'success' | 'fail' | 'overview' | 'loading') => void;
   handleSetDeliveryDate: Dispatch<SetStateAction<Date>>;
}

export const CheckoutStep: FC<Props> = ({
   features,
   deliveryAddress,
   legalAddress,
   locale,
   hasProblems,
   changeCheckoutStatus,
   handleSetDeliveryDate
}) => {
   type Step = 'deliveryAddress' | 'orderReference' | 'deliveryDate';
   const translate = useTranslations();
   const maximumDeliveryDate = new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate());

   const [currentStep, setCurrentStep] = useState<Step>('deliveryAddress');
   const [orderReference, setOrderReference] = useState<string>('');
   const [deliveryDate, setDeliveryDate] = useState<Date>(new Date(DateUtils.getNextBusinessDay(LocalDateTime.now(), 2, 16)));
   const [formattedDeliveryDate, setFormattedDeliveryDate] = useState<string>('');
   const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

   const showTermsAndConditions = hasPermission(features, 'sales-terms-and-conditions');

   useEffect(() => {
      setFormattedDeliveryDate(
         deliveryDate.toLocaleDateString(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
         })
      );
   }, [deliveryDate, locale]);

   const handleStepChange = (step: Step) => {
      setCurrentStep(step);
   };

   const handleCheckout = async (request: PostShoppingCartCheckout) => {
      changeCheckoutStatus('loading');
      // check if the cart has been emptied in another session
      const cart = await ShoppingCartUtils.getShoppingCart();
      if (!cart?.items.length) return changeCheckoutStatus('fail');

      try {
         const orderId = await ShoppingCartUtils.checkout(request);

         TrackingUtils.track('complete_checkout', {
            orderID: orderId,
            deliveryAddressId: request.deliveryAddressId.toString(),
            billingAddressId: request.billingAddressId.toString(),
            deliveryDate: request.requestedDeliveryDate.toString(),
            customerOrderReference: request.customerOrderReference
         });

         return changeCheckoutStatus('success');
      } catch (error) {
         const problems = (error as HttpErrorResponse).error as Array<ShoppingCartProblem>;
         const hasCheckoutProblems = problems?.length > 0 || !cart?.items?.length;
         return hasCheckoutProblems ? changeCheckoutStatus('fail') : changeCheckoutStatus('success');
      }
   };

   const handlePlaceOrder = async () => {
      const request: PostShoppingCartCheckout = {
         deliveryAddressId: deliveryAddress.id,
         billingAddressId: legalAddress.id,
         customerOrderReference: orderReference,
         requestedDeliveryDate: LocalDate.ofInstant(Instant.parse(new Date(deliveryDate).toISOString() || '')).toString()
      };
      await handleCheckout(request);
   };

   const deliveryAddressStep = (
      <Paper>
         <Box direction="column" gap="md">
            <Box gap="xs" justify="between">
               <Typography component="h4" variant="heading-4" color="primary">
                  {translate('shopping-cart.checkout.billing-and-delivery-step')}
               </Typography>
               {currentStep !== 'deliveryAddress' && (
                  <>
                     <BreakpointSwitch excludeViews={['desktop', 'tab-port', 'tab-land']}>
                        <Typography variant="caption">
                           <MultiLineText text={deliveryAddress.htmlFormat} lineBreak="<br>" />
                        </Typography>
                     </BreakpointSwitch>
                     <BreakpointSwitch excludeViews={['phone']}>
                        <Box direction="column" gap="none" items="end">
                           <Typography variant="caption">
                              <MultiLineText text={deliveryAddress.htmlFormat} lineBreak="<br>" />
                           </Typography>
                        </Box>
                     </BreakpointSwitch>
                  </>
               )}
            </Box>
            {currentStep === 'deliveryAddress' && (
               <>
                  <Typography color="subtle" variant="heading-4">
                     {translate('shopping-cart.checkout.your-billing-address')}
                  </Typography>
                  <Typography variant="small">
                     <MultiLineText text={deliveryAddress.htmlFormat} lineBreak="<br>" />
                  </Typography>
                  <Typography color="subtle" variant="heading-4">
                     {translate('shopping-cart.checkout.your-delivery-address')}
                  </Typography>
                  <Typography variant="small">
                     <MultiLineText text={legalAddress.htmlFormat} lineBreak="<br>" />
                  </Typography>
                  <Box justify="end" gap="sm">
                     <Button variant="primary" onClick={() => handleStepChange('orderReference')}>
                        {translate('shopping-cart.checkout.order-reference')} <Icon icon="long-arrow-down" />
                     </Button>
                  </Box>
               </>
            )}
         </Box>
      </Paper>
   );

   const orderReferenceStep = (
      <Paper>
         <Box direction="column" gap="md">
            <Box gap="xs" justify="between">
               <Typography component="h4" variant="heading-4" color="primary">
                  {translate('shopping-cart.checkout.order-reference-step')}
               </Typography>
               {currentStep !== 'orderReference' && <Typography variant="caption">{orderReference}</Typography>}
            </Box>
            {currentStep === 'orderReference' && (
               <>
                  <InputField
                     value={orderReference}
                     onChange={(inputValue) => setOrderReference(inputValue)}
                     id="shopping-cart-checkout-order-reference"
                     supportText={translate('shopping-cart.checkout.fill-in-internal-reference')}
                     label={translate('shopping-cart.checkout.optional-order-reference')}
                     maxLength={40}
                  />
                  <Box justify="end" gap="sm">
                     <Button variant="secondary" onClick={() => handleStepChange('deliveryAddress')}>
                        {translate('shopping-cart.checkout.previous')} <Icon icon="long-arrow-up" />
                     </Button>
                     <Button variant="primary" onClick={() => handleStepChange('deliveryDate')}>
                        {translate('shopping-cart.checkout.delivery-date')} <Icon icon="long-arrow-down" />
                     </Button>
                  </Box>
               </>
            )}
         </Box>
      </Paper>
   );

   const deliveryDateStep = (
      <Paper>
         <Box direction="column" gap="md">
            <Box gap="xs" justify="between">
               <Typography component="h4" variant="heading-4" color="primary">
                  {translate('shopping-cart.checkout.delivery-date-step')}
               </Typography>
               {currentStep !== 'deliveryDate' && <Typography variant="caption">{formattedDeliveryDate}</Typography>}
            </Box>
            {currentStep === 'deliveryDate' && (
               <>
                  <Typography>{formattedDeliveryDate}</Typography>
                  <Calendar
                     disabledDays={['Saturday', 'Sunday']}
                     value={deliveryDate}
                     onChange={(inputValue) => {
                        setDeliveryDate(inputValue);
                        handleSetDeliveryDate(inputValue);
                     }}
                     min={new Date(DateUtils.getNextBusinessDay(LocalDateTime.now()))}
                     max={maximumDeliveryDate}
                  />
                  {!showTermsAndConditions && (
                     <Box>
                        <Checkbox checked={termsAccepted} onChange={(checked) => setTermsAccepted(checked)}>
                           <Typography variant="body" component="p">
                              {translate('shopping-cart.checkout.i-agree-to-our')}
                              &nbsp;
                              {/* TODO: make this link configurable through the component  */}
                              <Link href={`/${locale}/sales-terms-and-conditions`}>{translate('shopping-cart.checkout.terms-and-conditions')}</Link>
                           </Typography>
                        </Checkbox>
                     </Box>
                  )}
                  <Box justify="end" gap="sm">
                     <Button variant="secondary" onClick={() => handleStepChange('orderReference')}>
                        {translate('shopping-cart.checkout.previous')} <Icon icon="long-arrow-up" />
                     </Button>
                     <Button disabled={hasProblems || !termsAccepted} variant="primary" onClick={handlePlaceOrder}>
                        {translate('shopping-cart.checkout.place-order')} <Icon icon="paper-plane" />
                     </Button>
                  </Box>
               </>
            )}
         </Box>
      </Paper>
   );
   return (
      <Box direction="column" gap="sm">
         {deliveryAddressStep}
         {orderReferenceStep}
         {deliveryDateStep}
      </Box>
   );
};
